/**
 * Use this to fetch data that is static and does not need to be re-fetched.
 *
 * `dedupe: 'defer'` rather than Nuxt's default `'cancel'`: cancel aborts the
 * in-flight request when a second call for the same key arrives — a link
 * prefetch racing the navigation is enough — and Nuxt resolves the aborted one
 * with neither data nor error, which reads here as "no data" and throws.
 * Joining the in-flight request is also the right answer for data that never
 * changes.
 */
interface UseStaticOptions {
  /**
   * Some fetches legitimately resolve to a falsy value (e.g. "no Contentful
   * entry matches this slug") and the caller already has its own handling for
   * that (typically a 404). Set this when a falsy result is meaningful data,
   * not a sign the fetch failed, so useStatic returns it instead of throwing.
   */
  allowNull?: boolean
}

// Overloads keep the return type non-nullable for existing callers (the default,
// throw-on-null contract) while callers that opt into `allowNull` get the honest
// `T | null | undefined` type for a result they've said they'll handle themselves.
export default function useStatic<T>(key: string, fn: () => Promise<T>): Promise<T>
export default function useStatic<T>(
  key: string,
  fn: () => Promise<T>,
  options: { allowNull: true },
): Promise<T | null | undefined>
export default async function useStatic<T>(
  key: string,
  fn: () => Promise<T>,
  options: UseStaticOptions = {},
) {
  const asyncData = await useAsyncData(key, fn, { server: true, dedupe: 'defer' })
  const { data, error, status } = asyncData

  // Neither data nor error, and not settled, means the promise we joined belonged
  // to an entry Nuxt has since disposed (`_off` clears `_init` but leaves the
  // in-flight promise), so its result landed on an object this call cannot read.
  // Fetch once rather than reporting a key that is not actually missing. A
  // 'success' status with no data is a real empty answer and needs no refetch.
  if (!data.value && !error.value && status.value !== 'success') {
    await asyncData.refresh({ dedupe: 'cancel' })
  }

  if (error.value) {
    throw error.value
  }
  if (!data.value && !options.allowNull) {
    throw new Error(`No data found for key '${key}'`)
  }
  return data.value
}
